import * as form from '../../../library/form';
import {
  validateBasicField,
  validateSelect
} from '../../../library/form/field-validations';
import * as c from './use-form-constants';

import {
  VALIDATE_FLEET_NUMBER,
  VALIDATE_POSTAL_CODE
} from '../../graphql/form-queries';

import * as v from '../../utils/validators/validators';

import {
  CANADA_ISO_CODE,
  COUNTRIES,
  PROVINCES,
  STATES
} from '../../constants/form';

function findAddressOptions(region, country) {
  const regionOptions = country === CANADA_ISO_CODE ? PROVINCES : STATES;
  const regionValue = regionOptions.find(
    ele => ele?.value === `${country}-${region}`
  );
  const countryValue = COUNTRIES.find(ele => ele?.value === country);

  return {
    country: countryValue,
    region: regionValue
  };
}

function validatePostalCodeWithService(client, postalCode) {
  return client.query({
    query: VALIDATE_POSTAL_CODE,
    variables: { postalCode: postalCode?.toUpperCase() }
  });
}

function updateZipRelatedFields(fields, newValues, updateField) {
  if (fields?.city) updateField(fields.city.name, newValues?.city);
  if (fields?.townCity) updateField(fields.townCity.name, newValues?.city);
  if (fields?.state) updateField(fields.state.name, newValues?.region);
  if (fields?.region) updateField(fields.region.name, newValues?.region);
  if (fields?.regionIso) updateField(fields.regionIso.name, newValues?.region);
  if (fields?.country) updateField(fields.country.name, newValues?.country);
  if (fields?.zip) updateField(fields.zip.name, newValues?.postalCode);
  if (fields?.postalCode)
    updateField(fields.postalCode.name, newValues?.postalCode);
}

function validateArFleetNumberWithService(client, fleetNumber) {
  return client.query({
    query: VALIDATE_FLEET_NUMBER,
    variables: { fleetNumber: fleetNumber?.padStart(10, '0') }
  });
}

function updateFleetRelatedFields(fields, newValues, updateField) {
  if (fields?.fleetAccountNumber && newValues?.fleetAccountNumber)
    updateField(fields.fleetAccountNumber.name, newValues.fleetAccountNumber);
  if (fields?.companyName && newValues?.companyName)
    updateField(fields.companyName.name, newValues.companyName);
  if (
    process.env.__FF_AUTOCOMPLETE_FLEET_COMPANY_ADDRESS__ === 'true' &&
    fields?.fleetAccountNumber?.autoComplete
  ) {
    if (fields?.addressLine1 && newValues?.addressLine1)
      updateField(fields.addressLine1.name, newValues.addressLine1);
    if (fields?.state && newValues?.state)
      updateField(fields.state.name, { value: newValues.state });
    if (fields?.country && newValues?.country)
      updateField(fields.country.name, newValues.country);
  }
}

async function validateZip(
  value = '',
  options = {},
  { getFieldsObject, updateField },
  client
) {
  let errorState = { ...form.defaultErrorState };
  const { fieldName = form.ZIP, isRequired = true } = options;

  if (!v.isValidPostalCode(value) && v.notEmpty(value)) {
    errorState = form.formUtils.createError(
      form.IS_VALID_POSTAL_CODE,
      fieldName,
      errorState.failedValidations
    );
  }

  if (!v.notEmpty(value) && isRequired) {
    errorState = form.formUtils.createError(
      form.NOT_EMPTY,
      fieldName,
      errorState.failedValidations
    );
  }

  if (!errorState.hasErrors) {
    await validatePostalCodeWithService(client, value).then(({ data }) => {
      if (data?.contactValidation?.postalCode) {
        const {
          city: postalCodeCity,
          state: postalCodeState,
          country: postalCodeCountry,
          postalCode,
          isValid
        } = data.contactValidation.postalCode;

        if (isValid) {
          const addressValues = findAddressOptions(
            postalCodeState,
            postalCodeCountry
          );
          const formFields = getFieldsObject();

          updateZipRelatedFields(
            formFields,
            {
              city: postalCodeCity,
              country: addressValues.country,
              postalCode: postalCode,
              region: addressValues.region
            },
            updateField
          );
        } else {
          errorState = form.formUtils.createError(
            form.IS_VALID_POSTAL_CODE,
            fieldName,
            errorState.failedValidations
          );
        }
      }
    });
  }

  return errorState;
}

async function validateFleetAccountNumber(
  value = '',
  options = {},
  { getFieldsObject, updateField },
  client
) {
  const { fieldName = c.FLEET_ACCOUNT_NUMBER } = options;
  let errorState = validateBasicField(value, { fieldName, ...options });
  if (!errorState.hasErrors && !v.isNumeric(value) && v.notEmpty(value)) {
    errorState = form.formUtils.createError(
      form.IS_VALID_NAME,
      fieldName,
      errorState.failedValidations
    );
  }

  if (!errorState.hasErrors && v.isNumeric(value) && value.length < 5) {
    errorState = form.formUtils.createError(
      form.IS_VALID_NAME,
      fieldName,
      errorState.failedValidations
    );
  }

  if (
    !errorState.hasErrors &&
    v.isNumeric(value) &&
    v.notEmpty(value) &&
    value.length >= 5
  ) {
    await validateArFleetNumberWithService(client, value).then(({ data }) => {
      if (data?.fleetValidation?.fleetNumber) {
        const {
          company: fleetCompany,
          companyAddress: fleetCompanyAddress,
          fleetNumber,
          isValid
        } = data.fleetValidation.fleetNumber;

        if (isValid) {
          const formFields = getFieldsObject();

          updateFleetRelatedFields(
            formFields,
            {
              addressLine1: fleetCompanyAddress?.line1,
              companyName: fleetCompany,
              country: fleetCompanyAddress?.country?.isocode,
              fleetNumber: fleetNumber,
              state: fleetCompanyAddress?.region?.isocode
            },
            updateField
          );
          if (
            process.env.__FF_AUTOCOMPLETE_FLEET_COMPANY_ADDRESS__ === 'true' &&
            fleetCompanyAddress?.postalCode &&
            formFields?.fleetAccountNumber?.autoComplete
          ) {
            validateZip(
              fleetCompanyAddress?.postalCode,
              options,
              { getFieldsObject, updateField },
              client
            );
          }
        } else {
          errorState = form.formUtils.createError(
            c.IS_VALID_FLEET_COMPANY,
            fieldName,
            errorState.failedValidations
          );
        }
      }
    });
  }

  if (errorState.hasErrors || value.length < 5) {
    const formFields = getFieldsObject();
    updateField(formFields.companyName.name, '', true);
    if (
      process.env.__FF_AUTOCOMPLETE_FLEET_COMPANY_ADDRESS__ === 'true' &&
      formFields?.fleetAccountNumber?.autoComplete &&
      value
    ) {
      updateField(formFields.addressLine1.name, '', true);
      updateField(formFields.country.name, '', true);
      updateField(formFields.city.name, '', true);
      updateField(formFields.zip.name, '', true);
      updateField(formFields.state.name, STATES[0], true);
    }
  }

  return errorState;
}

async function validateState(
  value = '',
  options = {},
  { getFieldsObject },
  client
) {
  let errorState = validateSelect(value, { fieldName: form.STATE, ...options });

  const formFields = getFieldsObject();
  const zipCode = formFields?.zip?.value || formFields?.postalCode?.value || '';

  if (!errorState.hasErrors && zipCode) {
    await validatePostalCodeWithService(client, zipCode).then(({ data }) => {
      if (data?.contactValidation?.postalCode) {
        const {
          state: postalCodeState,
          country: postalCodeCountry,
          isValid
        } = data.contactValidation.postalCode;

        if (isValid) {
          const addressValues = findAddressOptions(
            postalCodeState,
            postalCodeCountry
          );

          if (value.value !== addressValues.region.value) {
            errorState = form.formUtils.createError(
              form.IS_MATCHING_ZIP_TO_STATE,
              form.STATE,
              errorState.failedValidations
            );
          }
        }
      }
    });
  }
  return errorState;
}

export default {
  ...form.fieldValidations,
  validateFleetAccountNumber,
  validateState,
  validateZip
};
